import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ShipmentStatus, ShipmentFilter } from '../../types/shipment.types';
import { Button } from '../../components/Button';

interface ShipmentFiltersModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: ShipmentFilter) => void;
  initialFilters?: ShipmentFilter;
  campaigns: { id: number; name: string }[];
  organizations: string[];
}

/**
 * Modal component for filtering shipments
 * Includes filters for:
 * - Campaign (dropdown)
 * - Status (checkboxes)
 * - Organization (dropdown)
 * - Date range (from/to)
 * - Show deactivated toggle
 */
const ShipmentFiltersModal: React.FC<ShipmentFiltersModalProps> = ({
  open,
  onClose,
  onApply,
  initialFilters = {},
  campaigns,
  organizations
}) => {
  const [filters, setFilters] = useState<ShipmentFilter>(initialFilters);

  // Sync filters when props change
  useEffect(() => {
    if (open) {
      setFilters(initialFilters);
    }
  }, [initialFilters, open]);

  if (!open) return null;

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleClearAll = () => {
    // Keep sort settings but clear all other filters
    setFilters({
      sortBy: filters.sortBy || 'createdAt',
      sortOrder: filters.sortOrder || 'desc'
    });
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-white/50 dark:border-white/10 w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Filter Shipments</h2>
          <Button
            variant="tertiary"
            size="sm"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </Button>
        </div>
        
        <div className="space-y-4">
          {/* Campaign Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Campaign</label>
            <select
              className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md w-full p-2"
              value={filters.campaign ?? ''}
              onChange={(e) => setFilters({
                ...filters,
                campaign: e.target.value ? parseInt(e.target.value) : undefined
              })}
            >
              <option value="">All Campaigns</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
              ))}
            </select>
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <div className="space-y-2">
              {Object.values(ShipmentStatus).map(status => (
                <div key={status} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`status-${status}`}
                    checked={filters.status?.includes(status) || false}
                    onChange={(e) => {
                      const newStatuses = filters.status || [];
                      if (e.target.checked) {
                        setFilters({
                          ...filters,
                          status: [...newStatuses, status]
                        });
                      } else {
                        setFilters({
                          ...filters,
                          status: newStatuses.filter(s => s !== status)
                        });
                      }
                    }}
                    className="h-4 w-4 text-primary-100 border-gray-300 dark:border-white/10 dark:bg-slate-800 rounded"
                  />
                  <label htmlFor={`status-${status}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          {/* Organization Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization</label>
            <select
              className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md w-full p-2"
              value={filters.organization || ''}
              onChange={(e) => setFilters({
                ...filters,
                organization: e.target.value || undefined
              })}
            >
              <option value="">All Organizations</option>
              {organizations.map(org => (
                <option key={org} value={org}>{org}</option>
              ))}
            </select>
          </div>
          
          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">From</label>
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => setFilters({
                    ...filters,
                    dateFrom: e.target.value || undefined
                  })}
                  className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md w-full p-2"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">To</label>
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => setFilters({
                    ...filters,
                    dateTo: e.target.value || undefined
                  })}
                  className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md w-full p-2"
                />
              </div>
            </div>
          </div>
          
          {/* Show Deactivated Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="show-deactivated"
              checked={filters.showDeactivated || false}
              onChange={(e) => setFilters({
                ...filters,
                showDeactivated: e.target.checked || undefined
              })}
              className="h-4 w-4 text-primary-100 border-gray-300 dark:border-white/10 dark:bg-slate-800 rounded"
            />
            <label htmlFor="show-deactivated" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Show deactivated shipments
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 mt-6">
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={handleClearAll}
          >
            Clear All
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleApply}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  ,
  document.body
);
};

export default ShipmentFiltersModal;